import React, { useEffect, useMemo, useState } from 'react'
import { useReportWebVitals } from 'next/web-vitals'
import { AppContext as NextJSAppContext, AppProps } from 'next/app'
import { Provider } from 'react-redux'
import { createStore, applyMiddleware, compose, Store } from 'redux'
import thunk from 'redux-thunk'
import * as Sentry from '@sentry/react'
import rootReducers from '@store/redux/reducers'
import { analytics } from '@helpers/analytics'
import trackError from '@helpers/trackError'
import { isProdEnv, isBrowser } from '@helpers/env'
import AppComponent from '@components/App'
import '../components/global.styles.scss'
import {
  fetchContentfulHeaderFlashBanner,
  fetchContentfulHeader
} from '@api/graphqlContentFul/header'
import { getValueFromObject } from '@helpers/utils'
import { Router } from 'next/router'
import { EmptyObject, GenericFunction } from 'types/helpers'
import { PostHog } from '../global.d'
import { FlashBannerItem, MenuFlashBanner } from 'types/contentful/graphQlApi'
import AppContext from '@components/AppContext'
import { HeaderProps, HeaderTheme } from 'types/header'
import { LoginProvider } from '@api/accounts/auth/hooks/useLogin'
import { FitProvider } from '@helpers/hooks/useFit'
import { ABTestsProvider } from '@helpers/context/ABTestsContext'
import Script from 'next/script'

const NO_LAYOUT = ['fit-finder', 'returns']

const IGNORED_SENTRY_ERRORS = [
  'Failed to load https://www.googletagmanager.com/'
]

const isSandboxEnv = process.env.ENVIRONMENT === 'sandbox'

export function getFlashBannerItem(flashBanner: MenuFlashBanner | undefined) {
  const flashBannerResponse = getValueFromObject(
    flashBanner,
    'menu.items[0].flashBannerItem',
    {}
  )
  const flashBannerItem = {
    ...flashBannerResponse,
    showBanner: Object.keys(flashBannerResponse).length > 0
  }

  return flashBannerItem
}

export function getHeaderData(header: HeaderProps | undefined) {
  return getValueFromObject(header, 'menu.items[0].links', {})
}

function getIgnoredSentryError(eventValue: string): string | null {
  return IGNORED_SENTRY_ERRORS.find(error => eventValue.includes(error)) || null
}

if (isBrowser()) {
  Sentry.init({
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // Facebook borked
      'fb_xd_fragment',
      `Can't find variable: _AutofillCallbackHandler`
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Bing
      /bat.bing.com/i,
      // tiktok
      /analytics.tiktok.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Ignore Google flakiness
      /\/(gtm|ga|analytics)\.js/i,
      /googleads.g.doubleclick.net/i,
      /\/tag\.js/i,
      /assets\/smart-tag/i,
      /sezzle-widget.min.js/i,
      /dev.visualwebsiteoptimizer.com/i,
      /staticw2.yotpo.com/i,
      /googletagmanager.com/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^@safari-web-extension:/i
    ],
    enabled: process.env.NODE_ENV !== 'development',
    environment: process.env.API_ENV,
    release: process.env.NEXT_BUILD_ID,
    dist: process.env.NEXT_BUILD_ID,
    dsn: 'https://114158a216294a3aafff074e7efc0227@o490003.ingest.sentry.io/5553275',
    autoSessionTracking: true,
    integrations: [],
    beforeSend: event => {
      const exceptionValues = event.exception?.values

      if (
        exceptionValues &&
        exceptionValues.some(({ value }) => {
          if (!value) return false
          const ignoredError = getIgnoredSentryError(value)
          return ignoredError && value.includes(ignoredError)
        })
      ) {
        return null
      }
      return event
    }
  })

  window.postHogAsyncLoader = new Promise(resolve => {
    window.analytics?.ready(() => {
      if (process.env.NODE_ENV !== 'development') {
        window.posthog.init(process.env.POSTHOG_API_KEY, {
          api_host: 'https://eu.posthog.com',
          disable_session_recording: true,
          segment: window.analytics, // Pass window.analytics here - NOTE: `window.` is important
          capture_pageview: false, // You want this false if you are going to use segment's `analytics.page()` for pageviews
          rageclick: true, // capturing when a user clicks three or more times (maximum 1s between each click) without moving their mouse
          loaded: (posthog: PostHog) => {
            posthog.onFeatureFlags(() => {
              if (posthog.isFeatureEnabled('sc_recording')) {
                posthog.startSessionRecording()
              }
            })
            resolve(posthog)
          }
        })
      }
    })
  })
}

let devtools: GenericFunction = f => f
if (process.browser && window.__REDUX_DEVTOOLS_EXTENSION__ && !isProdEnv()) {
  devtools = window.__REDUX_DEVTOOLS_EXTENSION__()
}

const makeStore: (
  initialState: Record<string, unknown>
) => Store = initialState =>
  createStore(
    rootReducers,
    initialState,
    compose(
      applyMiddleware(thunk), // Add additional middleware here
      devtools
    )
  )

export const store = makeStore({})

export type FlashBanner =
  | FlashBannerItem
  | EmptyObject
  | { showBanner: boolean }

type PageProps = Record<string, unknown> & {
  statusCode: number
  pageEntries?: Array<Record<string, string | undefined>>
}

type AppOwnProps = {
  handle: string
  route: Router['route']
  statusCode: number
  flashBannerFromServer: FlashBanner
  headerFromServer: HeaderProps
  dixaToken: string
}

function MyApp({
  Component,
  pageProps,
  handle,
  route,
  flashBannerFromServer,
  headerFromServer,
  dixaToken,
  statusCode
}: AppProps & AppOwnProps) {
  const currentHeaderTheme = useMemo<HeaderTheme>(() => {
    const match = pageProps?.pageEntries?.find(
      (entry: { headerTheme?: HeaderTheme }) => entry?.headerTheme !== undefined
    )
    return (match?.headerTheme as HeaderTheme) ?? ('default' as HeaderTheme)
  }, [pageProps?.pageEntries])

  const [headerTheme, setHeaderTheme] = useState(currentHeaderTheme)
  const [header, setHeader] = useState(headerFromServer)
  const [flashBanner, setFlashBanner] = useState(flashBannerFromServer)

  useEffect(() => {
    setHeaderTheme(currentHeaderTheme)
  }, [currentHeaderTheme])

  useReportWebVitals(metric => {
    const props: Record<string, number | string | boolean> = {
      metric_name: metric.name,
      page_path: window.location.pathname
    }

    switch (metric.name) {
      case 'CLS':
        // send raw decimal (e.g., 0.12)
        props.cls = metric.value
        break
      case 'LCP':
        props.lcp_ms = metric.value // milliseconds
        break
      case 'INP':
        props.inp_ms = metric.value // milliseconds
        break
      case 'FCP':
        props.fcp_ms = metric.value // milliseconds
        break
      case 'TTFB':
        props.ttfb_ms = metric.value // milliseconds
        break
      case 'FID':
        props.fid_ms = metric.value // milliseconds
        break
    }

    analytics('web_vitals', props, {
      integrations: {
        All: false,
        'Google Analytics 4 Web': true
      }
    })
  })

  return (
    <Provider store={store}>
      <LoginProvider>
        <ABTestsProvider>
          <FitProvider>
            <AppContext.Provider
              value={{
                flashBanner,
                header,
                headerTheme,
                setHeaderTheme,
                setHeader,
                setFlashBanner
              }}
            >
              <AppComponent
                statusCode={statusCode}
                includeNoFollow={pageProps?.includeNoFollow}
                noLayout={
                  NO_LAYOUT.includes(handle) || NO_LAYOUT.includes(route)
                }
              >
                {!isSandboxEnv && (
                  <>
                    <Script
                      src="https://messenger.dixa.io/bootstrap.js"
                      strategy="lazyOnload"
                    />
                    <Script
                      id="dixa-init"
                      strategy="lazyOnload"
                      dangerouslySetInnerHTML={{
                        __html: `
                          (function (w, d, s) {
                            if (w._dixa_) return;
                            w._dixa_ = {
                              invoke: function (m, a) {
                                w._dixa_.pendingCalls = w._dixa_.pendingCalls || [];
                                w._dixa_.pendingCalls.push([m, a]);
                              }
                            };
                          })(window, document);
                          _dixa_.invoke('init', { messengerToken: '${dixaToken}' });
                        `
                      }}
                    />
                  </>
                )}
                <Component {...pageProps} />
              </AppComponent>
            </AppContext.Provider>
          </FitProvider>
        </ABTestsProvider>
      </LoginProvider>
    </Provider>
  )
}

MyApp.getInitialProps = async (
  context: NextJSAppContext
): Promise<AppOwnProps | undefined> => {
  try {
    const { router, Component } = context
    const isPreview = router.query.preview === 'true'

    const flashBanner = await fetchContentfulHeaderFlashBanner({
      preview: isPreview
    })

    const header = await fetchContentfulHeader({
      preview: isPreview
    })

    const componentPageProps = (
      Component.getInitialProps
        ? await Component.getInitialProps(context.ctx)
        : { statusCode: 200 }
    ) as PageProps

    const pageHandle = Array.isArray(router.query.pageHandle)
      ? router.query.pageHandle[0]
      : router.query.pageHandle || ''

    return {
      handle: pageHandle,
      flashBannerFromServer: getFlashBannerItem(flashBanner),
      route: context.router.route,
      statusCode: componentPageProps?.statusCode,
      headerFromServer: getHeaderData(header),
      dixaToken: process.env.DIXA_TOKEN
    }
  } catch (e) {
    const menuError = new Error('Could not get menu from contentful')
    const error = e instanceof Error ? e : menuError
    trackError(new Error('Could not get menu from contentful'), { error })
    return undefined
  }
}

export default MyApp
